import React, { useState, useEffect } from "react";
import StatusPill from "../components/common/StatusPill";
import { motion } from "framer-motion";
import {
  Bell,
  Send,
  History,
  Users,
  UserCheck,
  User,
  Clock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  Check,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { 
  sendNotificationToAll, 
  getNotificationHistory, 
  saveNotificationToFirestore,
  sendNotificationToUserIds 
} from "../services/notificationService";
import { getPatients } from "../services/patientService";
import { getDoctors } from "../services/doctorService";
import { cn } from "../lib/utils";

const NotificationsShadcn = () => {
  const [activeTab, setActiveTab] = useState("send");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all");
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Targeted notification state
  const [targetData, setTargetData] = useState([]);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (activeTab === "history") {
      loadNotificationHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (target === "specific_users" || target === "specific_doctors") {
      loadTargetData();
    } else {
      setTargetData([]);
      setSelectedTargetIds([]);
    }
  }, [target]);

  const loadTargetData = async () => {
    setIsLoadingTargets(true);
    try {
      let result;
      if (target === "specific_users") {
        result = await getPatients();
      } else {
        result = await getDoctors();
      }

      if (result.success) {
        setTargetData(result.data || []);
      } else {
        showToast("Error", `Failed to load ${target === "specific_users" ? 'patients' : 'doctors'}`, "destructive");
      }
    } catch (error) {
      console.error("Error loading target data:", error);
      showToast("Error", "An unexpected error occurred while loading recipients", "destructive");
    } finally {
      setIsLoadingTargets(false);
    }
  };

  const loadNotificationHistory = async () => {
    setLoadingHistory(true);
    try {
      const result = await getNotificationHistory();
      if (result.success) {
        setNotificationHistory(result.data || []);
      } else {
        setNotificationHistory([]);
      }
    } catch (error) {
      console.error("Error loading notification history:", error);
      setNotificationHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const showToast = (title, description, variant = "default") => {
    setToast({ title, description, variant, id: Date.now() });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      showToast("Validation Error", "Please fill in both title and message", "destructive");
      return;
    }

    setIsSending(true);

    try {
      // Save notification to Firestore first
      const saveResult = await saveNotificationToFirestore({
        title: title.trim(),
        message: message.trim(),
        target,
        status: "pending",
      });

      if (!saveResult.success) {
        console.error("Failed to save notification:", saveResult.error);
      }

      // Send notification via Cloud Function
      let result;
      if (target === "specific_users" || target === "specific_doctors") {
        if (selectedTargetIds.length === 0) {
          showToast("Validation Error", "Please select at least one recipient", "destructive");
          setIsSending(false);
          return;
        }
        result = await sendNotificationToUserIds(selectedTargetIds, title.trim(), message.trim());
      } else {
        result = await sendNotificationToAll(title.trim(), message.trim(), target);
      }

      if (result.success) {
        // Update notification status to sent
        if (saveResult.success && saveResult.id) {
          await saveNotificationToFirestore({
            id: saveResult.id,
            status: "sent",
            sentAt: new Date(),
            recipientsCount: result.data?.recipientsCount || 0,
          }, true); // true = update existing
        }

        showToast("Success", result.message || "Notification sent successfully to all devices", "success");

        // Reset form
        setTitle("");
        setMessage("");
        setTarget("all");

        // Reload history and switch to history tab
        setTimeout(() => {
          loadNotificationHistory();
          setActiveTab("history");
        }, 1500);
      } else {
        // Update notification status to failed
        if (saveResult.success && saveResult.id) {
          await saveNotificationToFirestore({
            id: saveResult.id,
            status: "failed",
            error: result.error,
          }, true);
        }

        // Show detailed error message
        const errorMsg = result.error || "Failed to send notification";
        showToast(
          "Error Sending Notification",
          errorMsg.includes("Cloud Function is not accessible")
            ? errorMsg
            : `Failed to send notification: ${errorMsg}`,
          "destructive"
        );
      }
    } catch (error) {
      showToast("Error", error.message || "An unexpected error occurred", "destructive");
    } finally {
      setIsSending(false);
    }
  };

  const getTargetIcon = (targetValue) => {
    switch (targetValue) {
      case "doctors":
      case "specific_doctors":
        return <UserCheck className="h-4 w-4" />;
      case "patients":
      case "specific_users":
        return <User className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getTargetLabel = (targetValue) => {
    switch (targetValue) {
      case "doctors":
        return "Doctors";
      case "patients":
        return "Patients";
      case "specific_users":
        return "Specific Users";
      case "specific_doctors":
        return "Specific Doctors";
      default:
        return "All Users";
    }
  };

  const toggleSelect = (id) => {
    setSelectedTargetIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredTargetData = targetData.filter(item => {
    const name = item.name || "";
    const email = item.email || "";
    const phone = item.phoneNumber || item.whatsappNumber || "";
    const query = searchQuery.toLowerCase();
    return name.toLowerCase().includes(query) || 
           email.toLowerCase().includes(query) || 
           phone.includes(query);
  });

  const selectAll = () => {
    setSelectedTargetIds(filteredTargetData.map(item => item.uid || item.id));
  };

  const deselectAll = () => {
    setSelectedTargetIds([]);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const tone = { sent: "green", pending: "amber", failed: "red" }[status] || "amber";
    return <StatusPill tone={tone} style={{ textTransform: "capitalize" }}>{status || "pending"}</StatusPill>;
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500 text-white shadow-md shadow-violet-500/25">
          <Bell size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Notifications</h1>
          <p className="text-sm text-muted-foreground">Send push notifications to your users and review what was sent</p>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
          <div style={{ padding: "12px 16px", borderRadius: 10, background: toast.variant === "success" ? "#ecfdf5" : "#fef2f2", border: `1px solid ${toast.variant === "success" ? "hsl(var(--tint-green-border))" : "hsl(var(--tint-red-border))"}`, color: toast.variant === "success" ? "#065f46" : "#ef4444", fontSize: 13, display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
            {toast.variant === "success" ? <CheckCircle2 size={16} style={{ marginTop: 2 }} /> : <AlertCircle size={16} style={{ marginTop: 2 }} />}
            <div>
              <strong style={{ display: "block", marginBottom: 2 }}>{toast.title}</strong>
              <span style={{ opacity: 0.9 }}>{toast.description}</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList style={{ background: "hsl(var(--muted))", padding: 4, borderRadius: 10 }}>
          <TabsTrigger value="send" style={{ borderRadius: 8, fontSize: 13, fontWeight: 600 }} className="flex items-center space-x-2 data-[state=active]:bg-card data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <Send size={14} /> <span>Send Notification</span>
          </TabsTrigger>
          <TabsTrigger value="history" style={{ borderRadius: 8, fontSize: 13, fontWeight: 600 }} className="flex items-center space-x-2 data-[state=active]:bg-card data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
            <History size={14} /> <span>History</span>
          </TabsTrigger>
        </TabsList>

        {/* Send Tab */}
        <TabsContent value="send">
          <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="admin-card" style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "hsl(var(--foreground))", margin: "0 0 4px 0" }}>Send Push Notification</h2>
              <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", margin: 0 }}>Broadcast notifications to all users, doctors, or patients</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Target Selection */}
              <div className="space-y-2">
                <Label htmlFor="target" style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Target Audience <span style={{ color: "#ef4444" }}>*</span></Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger id="target" style={{ borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)" }}>
                    <SelectValue placeholder="Select target audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center space-x-2"><Users size={14} /><span>All Users</span></div>
                    </SelectItem>
                    <SelectItem value="doctors">
                      <div className="flex items-center space-x-2"><UserCheck size={14} /><span>Doctors Only</span></div>
                    </SelectItem>
                    <SelectItem value="patients">
                      <div className="flex items-center space-x-2"><User size={14} /><span>Patients Only</span></div>
                    </SelectItem>
                    <SelectItem value="specific_users">
                      <div className="flex items-center space-x-2"><User size={14} /><span>Specific Users</span></div>
                    </SelectItem>
                    <SelectItem value="specific_doctors">
                      <div className="flex items-center space-x-2"><UserCheck size={14} /><span>Specific Doctors</span></div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", margin: 0 }}>
                  {target === "all" && "Send to all doctors and patients"}
                  {target === "doctors" && "Send to all doctors only"}
                  {target === "patients" && "Send to all patients only"}
                  {target === "specific_users" && "Select specific users to receive this notification"}
                  {target === "specific_doctors" && "Select specific doctors to receive this notification"}
                </p>
              </div>

              {/* Recipient Selection List for Specific Targets */}
              {(target === "specific_users" || target === "specific_doctors") && (
                <div className="space-y-3 mt-4 p-4 border rounded-xl bg-muted/40">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">
                      Select {target === "specific_users" ? "Users" : "Doctors"} ({selectedTargetIds.length} selected)
                    </Label>
                    <div className="flex space-x-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={selectAll}
                        className="h-7 text-xs"
                      >
                        Select All
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={deselectAll}
                        className="h-7 text-xs"
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={`Search ${target === "specific_users" ? "users" : "doctors"}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>

                  {/* Scrollable List */}
                  <div className="max-h-60 overflow-y-auto border rounded-lg bg-card">
                    {isLoadingTargets ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                      </div>
                    ) : filteredTargetData.length === 0 ? (
                      <div className="p-8 text-center text-sm text-gray-500">
                        No {target === "specific_users" ? "users" : "doctors"} found matching your search.
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredTargetData.map((item, index) => {
                          const id = item.uid || item.id || `item-${index}`;
                          const isSelected = selectedTargetIds.includes(id);
                          return (
                            <label 
                              key={id}
                              className={cn(
                                "flex items-center space-x-3 p-3 hover:bg-accent/60 cursor-pointer transition-colors border-b last:border-b-0 select-none",
                                isSelected && "bg-accent/70"
                              )}
                            >
                              <div className="flex-shrink-0">
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => toggleSelect(id)}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {item.name || "Unnamed User"}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {item.email || item.phoneNumber || item.whatsappNumber || "No contact info"}
                                </p>
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Title <span style={{ color: "#ef4444" }}>*</span></Label>
                <Input
                  id="title"
                  placeholder="Enter notification title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={100}
                  style={{ borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 13 }}
                  onFocus={e => e.currentTarget.style.background = "hsl(var(--card))"}
                  onBlur={e => e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"}
                />
                <p style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", margin: 0, textAlign: "right" }}>{title.length}/100</p>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Message <span style={{ color: "#ef4444" }}>*</span></Label>
                <Textarea
                  id="message"
                  placeholder="Enter notification message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  maxLength={500}
                  style={{ borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 13, resize: "none" }}
                  onFocus={e => e.currentTarget.style.background = "hsl(var(--card))"}
                  onBlur={e => e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"}
                />
                <p style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", margin: 0, textAlign: "right" }}>{message.length}/500</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSending || !title.trim() || !message.trim()}
                style={{ width: "100%", height: 44, borderRadius: 9, border: "none", background: isSending || !title.trim() || !message.trim() ? "hsl(var(--muted-foreground) / 0.6)" : "linear-gradient(180deg, hsl(217 71% 54%), hsl(222 72% 45%))", color: "white", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: isSending || !title.trim() || !message.trim() ? "not-allowed" : "pointer", transition: "background 0.2s" }}
              >
                {isSending ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending...</>
                ) : (
                  <><Send size={16} /> Broadcast Notification</>
                )}
              </button>
            </form>
          </div>

          {/* Live preview */}
          <div className="admin-card xl:sticky xl:top-24" style={{ padding: 20 }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live preview</p>
            <div className="mt-4 rounded-2xl border bg-muted/40 p-4">
              <div className="flex items-start gap-3 rounded-xl border bg-card p-3 shadow-sm">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">S</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Soocher</span>
                    <span className="text-[11px] text-muted-foreground">now</span>
                  </div>
                  <p className="mt-0.5 truncate text-sm font-semibold text-foreground">{title.trim() || "Notification title"}</p>
                  <p className="mt-0.5 line-clamp-3 text-[13px] text-muted-foreground">{message.trim() || "Your message will appear here as users see it on their phone."}</p>
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              Sent as a push notification to every device with a valid token for the chosen audience, and saved in the History tab.
            </p>
          </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1.5px solid hsl(var(--border))", background: "hsl(var(--muted) / 0.5)" }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "hsl(var(--foreground))", margin: "0 0 4px 0" }}>Notification History</h2>
              <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", margin: 0 }}>View previously sent notifications and their delivery status</p>
            </div>

            {loadingHistory ? (
              <LoadingSpinner message="Loading notification history..." />
            ) : notificationHistory.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <History size={40} style={{ color: "hsl(var(--input))", margin: "0 auto 12px" }} />
                <p style={{ fontSize: 15, fontWeight: 600, color: "hsl(var(--foreground))" }}>No notifications yet</p>
                <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Send your first notification to see history here.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: "hsl(var(--muted))", borderBottom: "1px solid hsl(var(--input))" }}>
                      {["Date Sent", "Target", "Notification", "Status", "Recipients"].map(h => (
                        <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notificationHistory.map((notification, idx) => (
                      <tr
                        key={notification.id}
                        style={{ borderBottom: "1px solid hsl(var(--muted))", background: idx % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.4)" }}
                      >
                        <td style={{ padding: "14px 16px", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Clock size={12} /> {formatDate(notification.createdAt)}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground) / 0.8)", background: "hsl(var(--muted))", padding: "4px 8px", borderRadius: 6, width: "fit-content" }}>
                            {getTargetIcon(notification.target || "all")}
                            {getTargetLabel(notification.target || "all")}
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px", maxWidth: 300 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 2 }}>{notification.title}</div>
                          <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{notification.message}</div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {getStatusBadge(notification.status)}
                          {notification.error && (
                            <div style={{ fontSize: 10, color: "#ef4444", marginTop: 4, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={notification.error}>
                              Error: {notification.error}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))" }}>
                          {notification.recipientsCount !== undefined ? notification.recipientsCount : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationsShadcn;

